import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Select, Spin, Typography } from "antd";
import { UserOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { types, errorAction } from "@authzsvc/api-lib";
import { showErrorNotification } from "../../util";
import { searchForPerson } from "../../actions/api";
import debounce from "lodash/debounce";

/**
 * Reusable identity search select component with debounced search
 * @param {Object} props
 * @param {string} [props.placeholder="Filter by identity"] - Placeholder text for the select
 * @param {Function} props.onSelect - Callback when an identity is selected
 * @param {Function} [props.onClear] - Callback when the selection is cleared
 * @param {Object} [props.value] - Currently selected identity object
 * @param {Object} [props.style] - Custom styles for the select
 * @param {string} [props.searchFilter] - Additional filter for searchForPerson API
 */
const IdentitySearchSelect = ({
  placeholder = "Filter by identity",
  onSelect,
  onClear,
  value,
  style,
  searchFilter,
}) => {
  const dispatch = useDispatch();
  const [identitySearchLoading, setIdentitySearchLoading] = useState(false);
  const [identitySearchResults, setIdentitySearchResults] = useState([]);
  const debouncedSearchRef = useRef();
  const identitySearchSeqRef = useRef(0);

  const handleIdentitySearch = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      // bump sequence to invalidate any in-flight requests
      identitySearchSeqRef.current++;
      setIdentitySearchResults([]);
      setIdentitySearchLoading(false);
      return;
    }
    debouncedSearchRef.current?.(searchText);
  };

  const handleClear = () => {
    // Invalidate any in-flight identity searches and clear results/loading
    identitySearchSeqRef.current++;
    setIdentitySearchResults([]);
    setIdentitySearchLoading(false);
    if (onClear) {
      onClear();
    }
  };

  useEffect(() => {
    debouncedSearchRef.current = debounce(async (searchText) => {
      // increment sequence id for this request
      const seq = ++identitySearchSeqRef.current;
      setIdentitySearchLoading(true);
      const response = await dispatch(
        searchFilter
          ? searchForPerson(searchText, "Person", null, null, searchFilter)
          : searchForPerson(searchText)
      );
      // if a newer search has started, ignore this response
      if (seq !== identitySearchSeqRef.current) return;
      if (response?.type === errorAction(types.SEARCH_FOR_PERSON)) {
        showErrorNotification(
          response.payload.title,
          response.payload.data.message
        );
      } else if (response?.payload) {
        setIdentitySearchResults(response.payload);
      }
      setIdentitySearchLoading(false);
    }, 800);

    return () => debouncedSearchRef.current?.cancel();
  }, [dispatch, searchFilter]);

  return (
    <Select
      showSearch
      suffixIcon={<UserOutlined />}
      placeholder={placeholder}
      style={style || { width: "100%" }}
      popupMatchSelectWidth={false}
      value={value ? value.displayName : undefined}
      onSearch={handleIdentitySearch}
      onSelect={(selectedValue, option) => onSelect(option.identity)}
      onClear={handleClear}
      allowClear
      loading={identitySearchLoading}
      filterOption={false}
      notFoundContent={
        identitySearchLoading ? <Spin size="small" /> : "No results"
      }
    >
      {identitySearchResults.map((identity) => (
        <Select.Option
          key={identity.id}
          value={identity.id}
          identity={identity}
          title={`${identity.displayName} - ${identity.upn}`}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <UserOutlined />
            <span>{identity.displayName}</span>
            {identity.upn && (
              <Typography.Text type="secondary">
                ({identity.upn})
              </Typography.Text>
            )}
          </span>
        </Select.Option>
      ))}
    </Select>
  );
};

IdentitySearchSelect.propTypes = {
  placeholder: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  value: PropTypes.object,
  style: PropTypes.object,
  searchFilter: PropTypes.string,
};

export default IdentitySearchSelect;
